// Supabase Auth Context — provides auth state to the entire app
// Single source of truth for authentication, session, and user profile
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from './config';
import { getUserDocument, onUserDocumentChange } from './database';
import type { UserDocument } from './types';
import i18n from '../i18n';

// ─── Context Type ─────────────────────────────────────────────────────────────
interface AuthContextType {
    // Supabase user (auth info)
    user: User | null;
    // Full user data from DB
    userDoc: UserDocument | null;
    // Convenience flags
    isLoggedIn: boolean;
    // Authoritative verification flag — sourced from public.users.is_email_verified
    // (our own flag; NOT trusted from Supabase's auto-confirmed email_confirmed_at).
    isEmailVerified: boolean;
    isLoading: boolean;
    // Refresh manually if needed
    refreshUserDoc: () => Promise<void>;
}

// ─── Create Context ────────────────────────────────────────────────────────────
const AuthContext = createContext<AuthContextType>({
    user: null,
    userDoc: null,
    isLoggedIn: false,
    isEmailVerified: false,
    isLoading: true,
    refreshUserDoc: async () => { },
});

// ─── Helper: apply user's saved language preference ──────────────────────────
function applyUserLanguage(doc: UserDocument | null) {
    const SUPPORTED = [
        'en', 'de', 'es', 'fr', 'pt', 'it', 'nl', 'sq', 'ar', 'tr',
        'ru', 'pl', 'sv', 'no', 'hi', 'zh', 'ja', 'ko', 'id', 'vi', 'th'
    ];
    const lang = doc?.language;
    if (lang && SUPPORTED.includes(lang) && i18n.language !== lang) {
        i18n.changeLanguage(lang);
    }
}

// ─── Auth Provider ─────────────────────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [userDoc, setUserDoc] = useState<UserDocument | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Determine whether the user should be force-signed-out based on their doc.
    // Admins are never kicked. Non-admins are kicked when:
    //   • is_blocked = true
    //   • subscription_status = 'overdue' (admin paused)
    //   • subscription_status = 'cancelled' AND cancelled_effective_date has passed
    // Returns a human-friendly message when a logout should occur, else null.
    const getForcedLogoutReason = (doc: UserDocument | null): string | null => {
        if (!doc) return null;
        if (doc.is_admin) return null;

        if (doc.is_blocked === true) {
            return 'Your account has been blocked by an administrator.';
        }
        if (doc.subscriptionStatus === 'overdue') {
            return 'Your subscription has been paused by an administrator. Please contact support.';
        }
        if (doc.subscriptionStatus === 'cancelled' && doc.cancelledEffectiveDate) {
            if (new Date(doc.cancelledEffectiveDate).getTime() <= Date.now()) {
                return 'Your subscription ended. Please re-subscribe to regain access.';
            }
        }
        return null;
    };

    // Fetch profile for a user
    const fetchProfile = useCallback(async (uid: string) => {
        try {
            console.log('[AuthContext] Fetching profile for', uid.slice(0, 8));
            const doc = await getUserDocument(uid);
            if (doc) {
                const forceLogoutMsg = getForcedLogoutReason(doc);
                if (forceLogoutMsg) {
                    console.warn('[AuthContext] Forcing sign-out:', forceLogoutMsg);
                    try { sessionStorage.setItem('auth_force_logout_reason', forceLogoutMsg); } catch { /* noop */ }
                    await supabase.auth.signOut();
                    setUser(null);
                    setUserDoc(null);
                    setIsLoading(false);
                    return;
                }
                console.log('[AuthContext] Profile loaded ✓ is_admin:', doc.is_admin);
                setUserDoc(doc);
                applyUserLanguage(doc);
            } else {
                console.warn('[AuthContext] No profile found for uid:', uid.slice(0, 8));
                setUserDoc(null);
            }
        } catch (err) {
            console.error('[AuthContext] Profile fetch failed:', err);
            setUserDoc(null);
        }
    }, []);

    // ── Single auth listener — sole driver of auth state ────────────────────────
    useEffect(() => {
        let mounted = true;

        // onAuthStateChange fires IMMEDIATELY with INITIAL_SESSION on setup,
        // so we don't need a separate bootstrap() call.
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                if (!mounted) return;
                console.log('[AuthContext] Auth event:', event, session?.user?.email ?? 'no-user');

                if (event === 'SIGNED_OUT') {
                    setUser(null);
                    setUserDoc(null);
                    setIsLoading(false);
                    return;
                }

                // INITIAL_SESSION (on page load/refresh) or SIGNED_IN (on login)
                if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
                    const sessionUser = session?.user ?? null;

                    if (!sessionUser) {
                        // No valid session
                        console.log('[AuthContext] No session — user is not logged in');
                        setUser(null);
                        setUserDoc(null);
                        setIsLoading(false);
                        return;
                    }

                    setUser(sessionUser);

                    // Fetch profile from DB
                    // Use setTimeout(0) to defer the DB call — this ensures the
                    // Supabase client's internal auth headers are updated before
                    // we make a DB query that might depend on JWT auth.
                    setTimeout(async () => {
                        if (!mounted) return;
                        await fetchProfile(sessionUser.id);
                        if (mounted) setIsLoading(false);
                    }, 0);
                    return;
                }

                // TOKEN_REFRESHED: only update the user object
                if (event === 'TOKEN_REFRESHED' && session?.user) {
                    setUser(session.user);
                }
            }
        );

        // Safety: if onAuthStateChange never fires (shouldn't happen), stop loading after 8s
        const timeout = setTimeout(() => {
            if (mounted && isLoading) {
                console.warn('[AuthContext] Timeout — forcing loading to false');
                setIsLoading(false);
            }
        }, 8000);

        return () => {
            mounted = false;
            subscription.unsubscribe();
            clearTimeout(timeout);
        };
    }, [fetchProfile]);

    // ── Realtime profile subscription ──────────────────────────────────────────
    // Separate from auth hydration to prevent re-render cascades
    useEffect(() => {
        if (!user) return;

        const unsubscribe = onUserDocumentChange(user.id, async (doc) => {
            // Force logout if admin just blocked / paused / cancelled (expired) this user
            const forceLogoutMsg = getForcedLogoutReason(doc);
            if (forceLogoutMsg) {
                console.warn('[AuthContext] Realtime force-logout:', forceLogoutMsg);
                try { sessionStorage.setItem('auth_force_logout_reason', forceLogoutMsg); } catch { /* noop */ }
                await supabase.auth.signOut();
                setUser(null);
                setUserDoc(null);
                return;
            }
            setUserDoc(doc);
            applyUserLanguage(doc);
        });

        return unsubscribe;
    }, [user?.id]);

    const refreshUserDoc = useCallback(async () => {
        if (!user) return;
        await fetchProfile(user.id);
    }, [user, fetchProfile]);

    const value: AuthContextType = {
        user,
        userDoc,
        isLoggedIn: !!user,
        // Email verification is enforced. The flag lives in public.users.is_email_verified
        // (set by our user_confirm_email_self RPC after the user clicks the link
        // in their email). Until then, the dashboard route guard redirects to
        // /auth?mode=verify so users cannot access the app until verified.
        isEmailVerified: userDoc?.isEmailVerified === true,
        isLoading,
        refreshUserDoc,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// ─── useAuth Hook ──────────────────────────────────────────────────────────────
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used inside AuthProvider');
    }
    return context;
}

export default AuthContext;
